/**
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import { TerminalPlatformsEnum } from './terminalPlatform';
import { SteerableBeamsResponse } from './steerableBeam';

// Min and Max Values
export const MIN_ELEVATION = -90;
export const MAX_ELEVATION = 90;
export const MIN_PITCH = -90;
export const MAX_PITCH = 90;
export const MIN_ROLL = -180;
export const MAX_ROLL = 180;
export const MIN_AZIMUTH = -180;
export const MAX_AZIMUTH = 360;
export const MIN_HEADING = 0;
export const MAX_HEADING = 360;
export const MIN_LATITUDE = -90;
export const MAX_LATITUDE = 90;
export const MIN_LONGITUDE = -180;
export const MAX_LONGITUDE = 180;
export const MAX_PING_LOSS_PERCENTAGE = 100;
export const ZERO = 0;

export default interface Terminal {
  terminalUuid: string;
  datapathUuid: string;
  modemUuid: string;
  terminalDisplayName: string;
  aircraftTailNumber: string;
  aircraftSerialNumber: string;
  antennaTypeId: number;
  fti: string;
  mtMacAddr: string;
  networkOrigin: 'Arclight-1' | 'Viasat' | 'ViaSat-3' | 'LinkWay' | 'I5_HCX' | 'GX';
  networkGroup: NetworkGroup;
  beamName: string;
  beamDisplayName: string;
  tthoSeconds: number;
  altitudeFeet: number;
  latitudeDeg: number;
  longitudeDeg: number;
  headingDeg: number;
  flDataRateBps: number;
  rlDataRateBps: number;
  connectivityStatus: string;
  flightPhase: string;
  sedFileVer: string;
  sscfFileVer: string;
  viHnaPolicyVersion: string;
  sourceTime: number;
  lastLoginTime: number;
  datapathUuidsOnAircraft: string[];
  visibilityBlanking: boolean;
  aprBlankingEnabled: boolean;
  restrictionEnabled: boolean;
  platformType: TerminalPlatformsEnum;
  haloNetSpaceVehicleName?: string;

  /* Position overlay fields, set in the frontend when the position shown came from HaloNet rather
     than from the terminal. Written together, and absent on every other terminal. */
  positionSourceTime?: number;
  isPositionOutsidePropagationWindow?: boolean;

  /* Additional Steerable Beam Information */
  isUsingSteerableBeam: boolean;
  steerableBeamPriority: SteerableBeamPriority | null | undefined;
  steerableBeamsSummary: SteerableBeamsResponse | null | undefined;

  primaryDataPath?: DataPath;
  installationType: InstallationType;
  terminalTypeLabel: TerminalTypeLabel;

  flSignalQuality: number | null | undefined;

  /* linkway fields */
  lwSiteId: string;
  lwTxPowerDb: number;

  /* GX fields */
  gxTpk: string;
}

export interface TerminalDetails extends Terminal {
  rollAngleDeg: number;
  pitchAngleDeg: number;
  azimuthDeg: number;
  elevationDeg: number;
  satellitesInFOV: SatelliteInFov[];
  nextBeam: string;
  nextBeamDisplayName: string;
  handoverDistanceMiles: number;
  nextHub: string;
  flSlaLowerBoundBps?: number;
  flSlaUpperBoundBps?: number;
  rlSlaLowerBoundBps?: number;
  rlSlaUpperBoundBps?: number;
  pingAvgLatency: number;
  packetLossPercentage: number;
  jitter: number;
  hubName: string;
  serviceRegionName: string;
  rlWaveform: string;
  visibilityBlanking: boolean;
  velocityKnots: number;
  antennaTypeId: number;
  antennaTypeName: string;
  modemType: string;
  terminalSwVer: string;
  satelliteName: string;
  viasatBeamName: string;
  terminalsInBeam: Terminal[];
  terminalsOnAircraft: TerminalDetails[];

  /* Terminal Traffic Current Metrics */
  trafficSourceTime: number;
  trafficCompSourceTime: number;
  trafficCompGapSec: number;
  flDataUseBytesOneMin: number;
  rlDataUseBytesOneMin: number;
  flDataUseBytes: number;
  rlDataUseBytes: number;
  flDataRateBps: number;
  rlDataRateBps: number;
  flRates: { [category: string]: number };
  rlRates: { [category: string]: number };

  /* Additional Terminal Metrics Needed for Charts on FTV */
  skewDeg: number;
  polarizationDeg: number;
  bdtVersion: string;
  pingTimestamp: number;
  contractNumber: string | null | undefined;
  contractStartSecondsSinceEpoch: number | null | undefined;
  contractEndSecondsSinceEpoch: number | null | undefined;

  /* linkway fields */
  lwSiteName: string;
  lwControlGroup: number;
  lwLnbLoFrequencyKHz: number;
  lwBucLoFrequencyKHz: number;
  lwLocalIpAddress: string;
  lwLocalSubnetMask: string;
  lwLocalGatewayIpAddress: string;
  lwSatelliteIpAddress: string;
  lwSatelliteSubnetMask: string;
  lwSatelliteRoutingProtocol: string;

  /* GX fields */
  gxMeetMePoint: string;
  gxTerminalRmp: string;
  gxSas: string;
  gxManufacturer: string;
  gxUptime: number;
  gxLatency: number;
  gxServiceAreaId: number;
  gxDid: string;
  gxDownstreamCirInKbps: number;
  gxDownstreamMirInKbps: number;
  gxUpstreamCirInKbps: number;
  gxUpstreamMirInKbps: number;
  gxTotalReceived: number;
  gxTotalTransmitted: number;
  gxPowerTransmitted: number;
  gxPowerReceived: number;
  gxPowerFootroom: number;
  gxPowerHeadroom: number;
  gxCurrentSnr: number;
  gxCalibratedSnr: number;
}

export type NetworkOrigin = Terminal['networkOrigin'];

export interface LatestTrafficPoint {
  trafficSourceTime: number;
  trafficCompSourceTime: number;
  flRates: { [category: string]: number };
  rlRates: { [category: string]: number };
  flDataRateBps: number;
  rlDataRateBps: number;
  gxCurrentSnr: number;
  gxCalibratedSnr: number;
  gxPowerHeadroom: number;
  gxPowerFootroom: number;
  flDataUseBytesOneMin: number;
  rlDataUseBytesOneMin: number;
  flUsage: { [category: string]: number };
  rlUsage: { [category: string]: number };
}

export interface SatelliteInFov {
  satelliteName: string;
  azimuthDeg: number;
  elevationDeg: number;
  hubName: string;
  dataPath: DataPath | undefined;
  connectivityStatus: string | undefined;
  link: string | undefined;
  isAlternative: boolean | undefined;
  steerableBeamsSummary: SteerableBeamsResponse | null | undefined;
  terminalUuid: string;
  datapathUuid: string;
  modemUuid: string;
}

// a Flight Path Waypoint is an element in the flight path array returned from /terminals/:datapathUuid/flightpath
export interface FlightPathWaypoint {
  sourceTime: number; // The dateTime that the VMT Periodic Message was sent in unix time (seconds since epoch)
  latitudeDeg: number; // The latitude in degrees of where the terminal was when the VMT Periodic Message was sent
  longitudeDeg: number; // The longitude in degrees of where the terminal was when the VMT Periodic Message was sent
  platformConnectivityStatus?: 'active' | 'online' | 'standby' | 'offline';
}

export interface SpaceNetworkInfo {
  networkName: string;
  networkAvailability: NetworkAvailability;
  networkMetricText: string;
}

export enum SteerableBeamPriority {
  PRIMARY = 1,
  SECONDARY = 2,
}

export enum InstallationType {
  SINGLE_TERMINAL,
  DUAL_TERMINAL,
  KU_PLUS_KA,
  HALONET_TERMINAL,
}

export enum DataPath {
  KU,
  KA,
  GX,
}

export enum TerminalTypeLabel {
  CWS1,
  CWS2,
  KUKA,
  KU,
  KA,
  MMT,
  TT,
  MRT,
  // Explicit so the values match the backend TerminalTypeLabel ids
  VI = 11,
  RTE = 12,
}

export enum NetworkGroup {
  VIASAT_ARCLIGHT = 'VIASAT_ARCLIGHT',
  LINKWAY = 'LINKWAY',
  GX = 'GX',
}

// TODO: change to number if sorting priority
export enum NetworkAvailability {
  AVAILABLE = 'available',
  UNAVAILABLE = 'unavailable',
  OFFLINE = 'offline',
}

// Used by the mock server for abstracting the SLA static data
export type serviceLevelAgreement = [number | undefined | null, number | undefined | null];
